import QRCode from 'qrcodejs2'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { GET } from '@/utils/Request'
import { GET_SIF_CARD } from '@/const/api'
import { SifTemplate } from '@/utils/SifTemplate'

class SifGenerator {
  constructor() {
    this.isGenerating = false
    this.container = null
  }

  async generateSif(onProgress = null, onError = null) {
  if (this.isGenerating) {
    throw new Error('Sif generation already in progress');
  }

  this.isGenerating = true;

  try {
    // Step 1: Fetch Sif data
    if (onProgress) onProgress('Fetching Sif data...', 10);
    const SifInfo = await this.fetchSif();

    // Step 2: Create temporary container
    if (onProgress) onProgress('Preparing Sif...', 35);
    this.createContainer();

    // Step 3: Render Sif HTML
    if (onProgress) onProgress('Rendering Sif...', 50);

    // ✅ Updated QR text as requested
    const qrText = `${process.env.VUE_APP_FRONTEND_URL}check-sif/${SifInfo.admission.hash_key}`;

    this.render(SifInfo,qrText);

    console.log('QR Text:', qrText);

    // Step 4: Generate QR code
    if (onProgress) onProgress('Generating QR code...', 60);
    await this.generateQRCodeInContainer(qrText);

    // Step 5: Wait for resources
    if (onProgress) onProgress('Loading fonts and images...', 70);
    await this.waitForResourcesLoaded();

    // Step 6: Generate PDF
    if (onProgress) onProgress('Generating PDF...', 85);
    await this.generatePDF(SifInfo.personal.username);

    // Step 7: Cleanup
    if (onProgress) onProgress('Download complete!', 100);
    this.cleanup();

    this.isGenerating = false;
  } catch (error) {
    this.cleanup();
    this.isGenerating = false;
    if (onError) onError(error);
    throw error;
  }
}


  formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

   formatTime(date) {
    let hour = date.getHours()
    const mins = String(date.getMinutes()).padStart(2, '0')
    let amPm = 'AM'

    if (hour > 11) {
      amPm = 'PM'
      if (hour > 12) {
        hour -= 12
      }
    }

    return `${hour}:${mins} ${amPm}`
  }

  render(SifInfo, qrText) {
  console.log('SifInfo', SifInfo)
  
  const html = SifTemplate(SifInfo, qrText);
  this.container.innerHTML = html;
}



  async fetchSif() {
    try {

      var response = await GET(GET_SIF_CARD);
      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to fetch Sif data');
      }

      const data = response.data; 

      return {
        personal: data.personal,
        academic: data.academic,
        guardian: data.guardian,
        admission: data.admission,
        xadmission: data.xadmission,
      };

    }catch (error) {
      console.error(error);
      throw error;
    }

  }


  createContainer() {
    // Remove existing container if any
    this.cleanup()

    // Create new container
    // A4 dimensions at 96 DPI (standard web DPI)
    const a4Width = 794 // 210mm in pixels
    const a4Height = 1123 // 297mm in pixels

    this.container = document.createElement('div')
    this.container.id = 'Sif-generator'
    this.container.style.position = 'absolute'
    this.container.style.left = '-9999px'
    this.container.style.top = '-9999px'
    this.container.style.width = a4Width + 'px'
    this.container.style.height = a4Height + 'px'
    this.container.style.backgroundColor = 'white'
    this.container.style.boxSizing = 'border-box'

    document.body.appendChild(this.container)
  }

  async generateQRCodeInContainer(qrText) {
    return new Promise((resolve) => {
      const qrCodeContainer = this.container.querySelector('#qrCodeUrl')

      if (qrCodeContainer) {
        // Clear any existing content
        qrCodeContainer.innerHTML = ''

        // Create canvas div for QR code
        const canvasDiv = document.createElement('div')
        canvasDiv.id = 'canvas'
        qrCodeContainer.appendChild(canvasDiv)

        // Generate QR code
        new QRCode('canvas', {
          text: qrText,
          width: 100,
          height: 100,
          colorDark: '#000000',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        })

        // Wait for QR code to render
        setTimeout(() => resolve(), 200)
      } else {
        resolve()
      }
    })
  }

  

  renderDigits(number) {
    const digits = String(number).split('')
    return digits.map((digit, index) => {
      const isLast = index === digits.length - 1
      return `<span class="roll-digit ${isLast ? 'roll-digit-last' : ''}">${digit}</span>`
    }).join('')
  }

  /**
   * Wait for all resources (fonts, images) to load
   */
  async waitForResourcesLoaded() {
    // Load font
    await document.fonts.load('16px "Tiro Bangla"')

    // Wait for all images to load
    const images = this.container.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) {
          return Promise.resolve()
        }
        return new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = () => {
            console.warn('Image failed to load:', img.src)
            resolve() // Resolve anyway to not block
          }
          // Timeout after 5 seconds
          setTimeout(resolve, 5000)
        })
      })
    )

    // Optimize images by reducing their display size
    //await this.optimizeImages()

    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 300))
  }


  async generatePDF(examRoll) {
  const element = this.container.querySelector('.sif-generator-wrapper');

  // Render the whole content to a canvas
  const canvas = await html2canvas(element, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#ffffff'
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.85);
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imgProps = pdf.getImageProperties(imgData);
  const pdfImgHeight = (imgProps.height * pdfWidth) / imgProps.width;

  let position = 0;
  let remainingHeight = pdfImgHeight;

  // Add pages
  while (remainingHeight > 0) {
    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfImgHeight);
    remainingHeight -= pdfHeight;
    position -= pdfHeight;

    if (remainingHeight > 0) {
      pdf.addPage();
    }
  }

  // ----------- Add Footer to last page -----------
  pdf.setPage(pdf.getNumberOfPages()); // go to last page
  const footerText = 'Powered by ICT Cell, University of Chittagong';
  pdf.setFontSize(9);
  pdf.setFont('Roboto Condensed', 'italic');



  // Position footer at bottom (10mm from bottom)
  //pdf.text(footerText, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
  pdf.text(footerText, 15, pdfHeight - 10);

  // Save PDF
  const filename = `cu_sif_${examRoll}.pdf`;
  pdf.save(filename);

  // const pdfBlob = pdf.output('blob');
  // const pdfUrl = URL.createObjectURL(pdfBlob);

  // // open outside your project
  // window.open(pdfUrl, "_blank");
}

cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }



}


export default new SifGenerator()